import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { api } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Analytics: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  // States for different analytics
  const [revenueData, setRevenueData] = useState<any>(null);
  const [examData, setExamData] = useState<any>(null);
  const [questionData, setQuestionData] = useState<any>(null);
  
  // States for Activity Logs
  const [logs, setLogs] = useState<any[]>([]);
  const [logPage, setLogPage] = useState(0);
  const [logRowsPerPage, setLogRowsPerPage] = useState(20);
  const [totalLogs, setTotalLogs] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  useEffect(() => {
    if (tabValue === 3) {
      loadLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logPage, logRowsPerPage]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      if (tabValue === 0) {
        if (!revenueData) {
          const res = await api.analytics.getRevenue();
          setRevenueData(res.data);
        }
      } else if (tabValue === 1) {
        if (!examData) {
          const res = await api.analytics.getExamPerformance();
          setExamData(res.data);
        }
      } else if (tabValue === 2) {
        if (!questionData) {
          const res = await api.analytics.getQuestionUsage();
          setQuestionData(res.data);
        }
      } else if (tabValue === 3) {
        loadLogs();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await api.analytics.getActivityLogs({
        page: logPage + 1,
        limit: logRowsPerPage,
      });
      setLogs(res.data.logs || []);
      setTotalLogs(res.data.total || res.data.pagination?.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatCurrency = (val: number) => `₹${val.toLocaleString()}`;

  const renderRevenue = () => {
    if (!revenueData) return null;
    return (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Total Revenue</Typography>
              <Typography variant="h4" color="success.main">{formatCurrency(revenueData.total_revenue || 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" gutterBottom>Revenue by Period</Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData.revenue_by_period || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <RechartsTooltip formatter={(val: any) => formatCurrency(Number(val))} />
                  <Legend />
                  <Bar dataKey="amount" fill="#2e7d32" name="Revenue Amount" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderExamPerformance = () => {
    if (!examData) return null;
    return (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Average Score</Typography>
              <Typography variant="h4" color="primary">
                {examData.overall_stats?.average_score?.toFixed(1) || 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Overall Pass Rate</Typography>
              <Typography variant="h4" color="success.main">
                {examData.overall_stats?.pass_rate?.toFixed(1) || 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" gutterBottom>Performance by Exam</Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={examData.performance_by_exam || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="exam_title" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="average_score" fill="#1976d2" name="Avg Score" />
                  <Bar dataKey="pass_rate" fill="#2e7d32" name="Pass Rate (%)" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderQuestionUsage = () => {
    if (!questionData) return null;
    return (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Total Questions</Typography>
              <Typography variant="h4">{questionData.total_questions || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Questions Used in Exams</Typography>
              <Typography variant="h4" color="primary">{questionData.questions_used || 0}</Typography>
              <Typography variant="body2" color="text.secondary">
                ({questionData.usage_rate?.toFixed(1) || 0}%)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
             <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>Most Used Questions</Typography>
             <TableContainer>
               <Table size="small">
                 <TableHead>
                   <TableRow>
                     <TableCell>Question ID</TableCell>
                     <TableCell>Text Snippet</TableCell>
                     <TableCell align="center">Usage Count</TableCell>
                     <TableCell align="center">Accuracy Rate</TableCell>
                   </TableRow>
                 </TableHead>
                 <TableBody>
                   {(questionData.most_used_questions || []).map((q: any) => (
                     <TableRow key={q.question_id}>
                       <TableCell>{q.question_id}</TableCell>
                       <TableCell>{q.question_text}</TableCell>
                       <TableCell align="center">{q.usage_count}</TableCell>
                       <TableCell align="center">{q.accuracy_rate?.toFixed(1) || 0}%</TableCell>
                     </TableRow>
                   ))}
                  </TableBody>
               </Table>
             </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderActivityLogs = () => {
    return (
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Activity Type</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">No logs found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                  <TableCell>
                    {log.user_name}
                    <Typography variant="caption" display="block" color="text.secondary">
                      ID: {log.user_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={log.activity_type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{log.description}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalLogs}
          page={logPage}
          onPageChange={(_, newPage) => setLogPage(newPage)}
          rowsPerPage={logRowsPerPage}
          onRowsPerPageChange={(e) => {
            setLogRowsPerPage(parseInt(e.target.value, 10));
            setLogPage(0);
          }}
        />
      </TableContainer>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Deep dive into platform metrics, performance, and logs
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Revenue" />
          <Tab label="Exam Performance" />
          <Tab label="Question Usage" />
          <Tab label="Activity Logs" />
        </Tabs>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          <TabPanel value={tabValue} index={0}>
            {renderRevenue()}
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {renderExamPerformance()}
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            {renderQuestionUsage()}
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            {renderActivityLogs()}
          </TabPanel>
        </>
      )}
    </Box>
  );
};

export default Analytics;
